import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner, Table } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import { formatBytes } from '../../helpers/formatHelpers';

// Reference ticker name as the backend sees it. Kept in sync with
// TickersReportService.ReferencePackageTickerName so the page can render
// a hint to the user about which ticker is the baseline.
const REFERENCE_TICKER_NAME = 'S&P 500 1950';

// Formats an ISO timestamp / Date as a localised string. Returns the
// em-dash placeholder when the value is missing or invalid so the table
// stays aligned.
const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

// Pulls the server-side error message out of an ApiClient exception.
// Mirrors the pattern used by AdminPackageTickersDatesValidation.
const describeError = (exc) => {
  if (!exc) return 'Unknown error';
  if (exc.structuredErrors && exc.structuredErrors.length > 0) {
    return exc.structuredErrors.map((e) => e.Message).join('; ');
  }
  return exc.message || 'Unknown error';
};

export const AdminPackageTickersValidation = () => {
  const [reports, setReports] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [latestReport, setLatestReport] = useState(null);

  // Pull the list of existing reports once on mount and after every
  // successful generation so the table refreshes without a manual
  // reload click.
  const loadReports = useCallback(async () => {
    try {
      setLoadingList(true);
      setListError(null);
      const range = await apiClient.listTickersReports({ offset: 0, limit: 100 });
      setReports((range && range.Items) || []);
    } catch (exc) {
      console.error('Failed to load tickers reports', exc);
      setListError(describeError(exc));
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleGenerate = async () => {
    setGenerateError(null);
    setLatestReport(null);
    try {
      setGenerating(true);
      const report = await apiClient.generateTickersReport({});
      setLatestReport(report);
      // Refresh the list so the just-generated report shows up at the
      // top of the table without requiring a manual page reload.
      await loadReports();
    } catch (exc) {
      console.error('Failed to generate tickers report', exc);
      setGenerateError(describeError(exc));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (id) => {
    apiClient.downloadTickersReport(id);
  };

  return (
    <div>
      <h2 className="mb-3">Package tickers validation</h2>

      <p className="text-muted">
        Click <strong>Generate report</strong> to compare every package ticker
        against the reference ticker <code>{REFERENCE_TICKER_NAME}</code>. The
        report uses the reference ticker&apos;s most-recent day candle date as
        the baseline and lists which other package tickers have that date in
        their data and which do not. The result is rendered as a PDF and
        stored in the database for later download.
      </p>

      <div className="parameters-pane mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="d-flex align-items-center">
          <Button color="primary" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generating…
              </>
            ) : (
              'Generate report'
            )}
          </Button>
          <span className="ms-3 text-muted small">
            Reference ticker:&nbsp;
            <code>{REFERENCE_TICKER_NAME}</code>
          </span>
        </div>
      </div>

      {generateError && (
        <Alert color="danger" fade={false}>
          Failed to generate report: {generateError}
        </Alert>
      )}

      {latestReport && !generateError && (
        <Alert color="success" fade={false}>
          Report <strong>#{latestReport.Id}</strong> generated successfully.
          Reference date:&nbsp;
          <strong>{formatDate(latestReport.ReferenceDate)}</strong>,
          total package tickers:&nbsp;
          <strong>{latestReport.TotalPackageTickers}</strong>,
          has date:&nbsp;
          <strong className="text-success">{latestReport.HasDateCount}</strong>,
          missing date:&nbsp;
          <strong className="text-danger">{latestReport.MissingDateCount}</strong>.
        </Alert>
      )}

      {listError && (
        <Alert color="warning" fade={false}>
          Failed to load existing reports: {listError}
        </Alert>
      )}

      <h3 className="mb-2">Available reports</h3>
      {loadingList && reports.length === 0 ? (
        <div className="text-center py-4">
          <Spinner />
          <p className="mt-2 text-muted">Loading reports…</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-muted py-3">
          No reports generated yet. Click <strong>Generate report</strong> to create the first one.
        </div>
      ) : (
        <Table bordered hover responsive size="sm" className="mb-0">
          <thead>
            <tr>
              <th style={{ width: 70 }}>Id</th>
              <th>Reference ticker</th>
              <th>Reference date</th>
              <th>Total</th>
              <th>Has date</th>
              <th>Missing date</th>
              <th>PDF size</th>
              <th>Generated at</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.Id}>
                <td>#{r.Id}</td>
                <td>
                  <div className="fw-semibold">{r.ReferencePackageTickerName}</div>
                  <div className="text-muted small">
                    <code>{r.ReferencePackageTickerCode}</code>
                    &nbsp;(PackageTicker #{r.ReferencePackageTickerId})
                  </div>
                </td>
                <td>{formatDate(r.ReferenceDate)}</td>
                <td>{r.TotalPackageTickers}</td>
                <td>
                  <Badge color="success">{r.HasDateCount}</Badge>
                </td>
                <td>
                  {r.MissingDateCount > 0 ? (
                    <Badge color="danger">{r.MissingDateCount}</Badge>
                  ) : (
                    <Badge color="secondary">0</Badge>
                  )}
                </td>
                <td className="text-muted small">{formatBytes(r.PdfSize || 0)}</td>
                <td className="text-muted small">{formatDateTime(r.GeneratedAt)}</td>
                <td>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleDownload(r.Id)}
                    disabled={!r.PdfSize}
                  >
                    Download PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};
